import { classPacer, poll, showInterval, type PollDeps } from './poll';
import type { PublicResultsClassFS, PublicShowFS } from './types';

/**
 * All data access. The widget reads the public projection (`publicResults`,
 * `publicShows`) through the public-data service at data.equestrian.digital, not
 * from Firestore. The Cloudflare Worker there keeps a short cached copy, so the
 * number of Firestore reads does not grow with the number of viewers.
 *
 * Code: services/public-data. Paths: /v1/widget/classes/{id}, /v1/widget/shows/{id}.
 * `VITE_PUBLIC_DATA_URL` points the dev server at a local copy of the service.
 */
const DATA_URL = `${import.meta.env.VITE_PUBLIC_DATA_URL ?? 'https://data.equestrian.digital'}/v1/widget`;

export type Unsubscribe = () => void;

const browser: PollDeps = {
  // The browser sends If-None-Match from its own cache, so an unchanged response is a short 304.
  fetch: (url, init) => fetch(url, { signal: init.signal, headers: { Accept: 'application/json' } }),
  isHidden: () => document.hidden,
  now: () => Date.now(),
  setTimeout: (callback, ms) => window.setTimeout(callback, ms),
  clearTimeout: (handle) => window.clearTimeout(handle as number | undefined),
};

/** Accepts `123` or `us_123` so an embed code can use either form. */
export function normalizeId(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  return trimmed.startsWith('us_') ? trimmed : `us_${trimmed}`;
}

/**
 * `onData` gets null when the class is not published (404). `onError` comes only
 * after several failed reads, and only when nothing has loaded yet.
 */
export function watchClass(
  classId: string,
  onData: (data: PublicResultsClassFS | null) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  return poll<PublicResultsClassFS>(browser, {
    url: `${DATA_URL}/classes/${encodeURIComponent(classId)}`,
    intervalOf: classPacer(browser.now),
    onData,
    onError,
  });
}

export function watchShow(
  showId: string,
  onData: (data: PublicShowFS | null) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  return poll<PublicShowFS>(browser, {
    url: `${DATA_URL}/shows/${encodeURIComponent(showId)}`,
    intervalOf: (value) => showInterval(value, browser.now()),
    onData,
    onError,
  });
}
